/**
 * Catálogo central dos links Eduzz para as 6 pós-graduações.
 *
 * Produtos fixos (out/2026) — checkouts em `sun.eduzz.com`:
 *   - **unica** (à vista / parcelado no cartão até 10x):
 *     cupom `LOTEPROMO` + `df=1&pf=1&np=10`
 *   - **assinatura** (recorrência mensal):
 *     cupom `LOTEPROMOASS` + `df=1`
 *
 * Variantes de captura:
 *   - **padrao** (default): usado em todo o site principal e nas LPs Wix
 *     principais das pós. Cupons `LOTEPROMO` / `LOTEPROMOASS`.
 *   - **gg**: usado APENAS nas páginas duplicadas de tráfego Google Ads
 *     (/pos-gg e LPs Wix /gg de cada pós). Cupons `LOTEPROMOGG` /
 *     `LOTEPROMOASSGG` — permitem controlar as vendas do Google separadamente.
 *
 * Estes links são usados pelo `<CheckoutButtons />` e `<PreCheckoutDialog />`
 * em todas as LPs. Sempre passam pelo pré-checkout para captura do lead
 * antes de redirecionar para a Eduzz.
 */

import type { ProductSlug, TipoCompra } from "@/components/checkout/PreCheckoutDialog";
import { blackScheduledHref } from "@/lib/black2x1/offer-rules";

export type CheckoutLinkSet = Record<TipoCompra, string>;
export type CheckoutVariant = "padrao" | "gg";

const CUPOM: Record<CheckoutVariant, Record<TipoCompra, string>> = {
  padrao: { unica: "LOTEPROMO", assinatura: "LOTEPROMOASS" },
  gg: { unica: "LOTEPROMOGG", assinatura: "LOTEPROMOASSGG" },
};

function build(eduzzId: string, tipo: TipoCompra, variant: CheckoutVariant) {
  const cupom = CUPOM[variant][tipo];
  if (tipo === "unica") {
    return `https://sun.eduzz.com/${eduzzId}?cupom=${cupom}&df=1&pf=1&np=10`;
  }
  return `https://sun.eduzz.com/${eduzzId}?cupom=${cupom}&df=1`;
}

// IDs Eduzz das turmas novas (dez/2026) — mesmos IDs para padrão e GG,
// o que diferencia as vendas é o cupom aplicado.
const EDUZZ_IDS: Record<ProductSlug, { unica: string; assinatura: string }> = {
  orto: { unica: "60E2E8Q3W3", assinatura: "E0DK6ODR91" },
  dor: { unica: "Q9N25BN101", assinatura: "D0R8A7P69Y" },
  esporte: { unica: "G96R1DJQW1", assinatura: "89AXQE7G0D" },
  geronto: { unica: "1W32Z6OG92", assinatura: "60E2E8Z3W3" },
  trauma: { unica: "39YNDV54WO", assinatura: "E9OGOY6KWB" },
  prescricao: { unica: "R9JXJ6QE0X", assinatura: "KW8ZKYJO01" },
};

export const POS_INFO: Record<
  ProductSlug,
  { slug: ProductSlug; nome: string; nomeCurto: string; wix: string }
> = {
  orto: {
    slug: "orto",
    nome: "Pós em Fisioterapia em Ortopedia e Traumatologia",
    nomeCurto: "Ortopedia",
    wix: "https://www.pos-orto.fisioemortopedia.com.br/",
  },
  dor: {
    slug: "dor",
    nome: "Pós em Fisioterapia em Dor",
    nomeCurto: "Dor",
    wix: "https://www.pos-dor.fisioemortopedia.com.br/",
  },
  esporte: {
    slug: "esporte",
    nome: "Pós em Fisioterapia Esportiva",
    nomeCurto: "Esporte",
    wix: "https://www.pos-esporte.fisioemortopedia.com.br/",
  },
  geronto: {
    slug: "geronto",
    nome: "Pós em Fisioterapia em Gerontologia",
    nomeCurto: "Gerontologia",
    wix: "https://www.pos-geronto.fisioemortopedia.com.br/",
  },
  trauma: {
    slug: "trauma",
    nome: "Pós em Fisioterapia em Traumatologia",
    nomeCurto: "Traumato",
    wix: "https://www.pos-traumato.fisioemortopedia.com.br/",
  },
  prescricao: {
    slug: "prescricao",
    nome: "Pós em Prescrição de Exercícios",
    nomeCurto: "Prescrição",
    wix: "https://www.pos-prescricao.fisioemortopedia.com.br/",
  },
};

function buildAll(variant: CheckoutVariant): Record<ProductSlug, CheckoutLinkSet> {
  return (Object.entries(EDUZZ_IDS) as Array<[ProductSlug, { unica: string; assinatura: string }]>)
    .reduce(
      (acc, [slug, ids]) => {
        acc[slug] = {
          unica: build(ids.unica, "unica", variant),
          assinatura: build(ids.assinatura, "assinatura", variant),
        };
        return acc;
      },
      {} as Record<ProductSlug, CheckoutLinkSet>,
    );
}

/** Links padrão (site principal, LPs Wix principais). */
export const CHECKOUT_LINKS: Record<ProductSlug, CheckoutLinkSet> = buildAll("padrao");

/** Links Google Ads (páginas duplicadas /gg no Wix e /pos-gg no site). */
export const CHECKOUT_LINKS_GG: Record<ProductSlug, CheckoutLinkSet> = buildAll("gg");

import { getOverride } from "@/lib/site-overrides";
import { getActiveCampaignClient, applyCampaignCoupon, campaignCouponFor } from "@/lib/checkout/campanhas";

/**
 * Vendas das turmas atuais encerradas (06/10/2026): enquanto os novos produtos
 * não chegam, todo checkout das 6 pós (padrão e GG) leva à LP da Black.
 * Para reabrir, troque para `false` e atualize EDUZZ_IDS.
 */
export const POS_VENDAS_PAUSADAS = true;

export function blackListaDestino(slug: ProductSlug, variant: CheckoutVariant, tipo: TipoCompra): string {
  // Sem UTMs próprias: o clique anexa a origem real da visita (origem-visita.ts).
  const p = new URLSearchParams({ ponto: `${variant === "gg" ? "checkout-pos-gg" : "checkout-pos"}-${slug}-${tipo}` });
  return blackScheduledHref(`https://fisioemortopedia.com.br/black-lista?${p.toString()}`, Date.now());
}

export function getCheckoutLinks(
  slug: ProductSlug,
  variant: CheckoutVariant = "padrao",
): CheckoutLinkSet {
  if (POS_VENDAS_PAUSADAS) {
    return { unica: blackListaDestino(slug, variant, "unica"), assinatura: blackListaDestino(slug, variant, "assinatura") };
  }
  const base = (variant === "gg" ? CHECKOUT_LINKS_GG : CHECKOUT_LINKS)[slug];
  const overrideKey = variant === "gg" ? `checkout-gg.${slug}` : `checkout.${slug}`;
  const unicaOverride = getOverride<{ url?: string }>(`${overrideKey}.unica`)?.url;
  const assinaturaOverride = getOverride<{ url?: string }>(`${overrideKey}.assinatura`)?.url;

  let unica = unicaOverride ?? base.unica;
  let assinatura = assinaturaOverride ?? base.assinatura;

  // Campanha ativa: troca o cupom do fluxo padrão e também do fluxo Google
  // quando a ação define cupom próprio para ele (ex: SEMANADAFISIOGG).
  const camp = getActiveCampaignClient();
  const cupomUnica = campaignCouponFor(camp, variant, "unica");
  const cupomAssinatura = campaignCouponFor(camp, variant, "assinatura");
  if (cupomUnica) unica = applyCampaignCoupon(unica, cupomUnica);
  if (cupomAssinatura) assinatura = applyCampaignCoupon(assinatura, cupomAssinatura);

  return { unica, assinatura };
}

export function getPosInfo(slug: ProductSlug) {
  return POS_INFO[slug];
}

export const ALL_PRODUCTS: ProductSlug[] = [
  "orto",
  "dor",
  "esporte",
  "geronto",
  "trauma",
  "prescricao",
];
